import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  Users, 
  Building2, 
  AlertTriangle, 
  MessageSquare,
  ArrowRight,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, description, color = "primary" }) => (
  <Card className="border-border" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-3xl font-bold tracking-tight">{value}</p>
          {description && (
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-lg bg-${color}/10 flex items-center justify-center`}>
          <Icon className={`h-6 w-6 text-${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/alerts?status=pending`)
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Tableau de bord" subtitle="Vue d'ensemble">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="border-border">
              <CardContent className="pt-6">
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-muted rounded w-1/2" />
                  <div className="h-8 bg-muted rounded w-1/3" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Tableau de bord" subtitle="Vue d'ensemble de la plateforme">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="stats-grid">
        <StatCard 
          title="Clients" 
          value={stats?.total_clients || 0} 
          icon={Users}
          description="Cabinets actifs"
        />
        <StatCard 
          title="Laboratoires" 
          value={stats?.total_labs || 0} 
          icon={Building2}
          description="Labos enregistrés"
        />
        <StatCard 
          title="Alertes" 
          value={stats?.pending_alerts || 0} 
          icon={AlertTriangle}
          description="En attente"
          color="destructive"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card className="border-border" data-testid="quick-actions">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold">Actions rapides</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/admin/clients">
              <Button variant="outline" className="w-full justify-between group" data-testid="add-client-action">
                <span className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Ajouter un client
                </span>
                <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </Button>
            </Link>
            <Link to="/admin/labs">
              <Button variant="outline" className="w-full justify-between group" data-testid="add-lab-action">
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Ajouter un laboratoire
                </span>
                <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Alerts */}
        <Card className="border-border" data-testid="recent-alerts">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Alertes récentes</CardTitle>
            <Link to="/admin/alerts">
              <Button variant="ghost" size="sm" className="text-muted-foreground">
                Voir tout
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                <p className="text-sm">Aucune alerte en attente</p>
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <div 
                    key={alert.id} 
                    className={`p-3 rounded-lg border ${
                      alert.severity === 'high' ? 'risk-high' : 'risk-medium'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{alert.risk_details}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          <Clock className="h-3 w-3 inline mr-1" />
                          {new Date(alert.created_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                      <Badge 
                        variant={alert.severity === 'high' ? 'destructive' : 'secondary'}
                        className="shrink-0"
                      >
                        {alert.severity === 'high' ? 'Élevé' : 'Moyen'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Activity Summary */}
      <Card className="mt-6 border-border" data-testid="activity-summary">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Activité du jour
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg flex-1">
              <p className="text-3xl font-bold">{stats?.messages_today || 0}</p>
              <p className="text-sm text-muted-foreground">Messages échangés</p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg flex-1">
              <p className="text-3xl font-bold">{stats?.linked_channels || 0}</p>
              <p className="text-sm text-muted-foreground">Canaux actifs</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default Dashboard;
